import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';

import { LoadFailure } from '../../components/LoadFailure';
import { TableLoading } from '../../components/TableLoading';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { TopBar } from '../IndividualPlan/TopBar';
import { FilterSelect } from './FilterSelect';
import {
  ALL,
  benchmarkFilterOptions,
  classOptions,
  clearedQuery,
  DEFAULT_QUERY,
  emptyBody,
  gradeOptions,
  headerMeta,
  pageCount,
  pageRows,
  printLabel,
  schoolsOf,
  teacherOptions,
  visibleRows,
  type BenchmarkFilter,
  type ListQuery,
} from './listRows';
import {
  bannerLabel,
  hiddenSelectedCount,
  loadListState,
  saveListState,
  toggleSelected,
  withoutSelected,
  withSelected,
  type ListState,
} from './listState';
import { PrintSectionsDialog, sectionIds, usePrintSections } from './PrintSectionsDialog';
import { SelectionBanner } from './SelectionBanner';
import { currentIdentity, fetchProgressReportStudents, loadProgressReportStudents, onProgressReportsReset, rememberedSchools, schoolLabel, type AuthSnapshot, type ListFetch, type ProgressReportStudents } from './students';
import { logEvent } from '../../utils/eventLog';
import { StudentsTable } from './StudentsTable';
import { TextButton } from './TextButton';
import { LIST } from './tokens';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

export const PRINT_PATH = '/reports/student-progress/print';
export const LIST_PATH = '/reports/student-progress';

type Loaded = { kind: 'loading' } | { kind: 'ready'; data: ProgressReportStudents } | { kind: 'error' };
const NO_STUDENTS: ProgressReportStudents['students'] = [];
const EMPTY_STATE: ListState = { query: DEFAULT_QUERY, selected: [] };
type StateStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function PrintButton({ count, label, onClick }: { count: number; label?: string; onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      disabled={count === 0}
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        height: 40,
        px: '20px',
        border: 0,
        borderRadius: '8px',
        bgcolor: LIST.primary,
        color: '#FFFFFF',
        fontFamily: LIST.font,
        fontSize: 16,
        lineHeight: '20px',
        fontWeight: 600,
        cursor: 'pointer',
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '&:disabled': { bgcolor: LIST.disabledBg, color: LIST.disabledFg, cursor: 'default' },
      }}
    >
      <PrintOutlinedIcon sx={{ width: 20, height: 20 }} aria-hidden="true" />
      {label ?? printLabel(count)}
    </Box>
  );
}

export function ProgressReportsListPage({
  source = fetchProgressReportStudents,
  storage,
  sectionsStorage,
  identity = currentIdentity,
}: {
  source?: (auth: AuthSnapshot, query: ListFetch) => Promise<ProgressReportStudents>;
  storage?: StateStorage | null;
  sectionsStorage?: StateStorage | null;
  identity?: () => Promise<string>;
}) {
  const navigate = useNavigate();
  const sections = usePrintSections(identity, sectionsStorage);
  const savedState = () => (storage === undefined ? loadListState() : loadListState(storage));
  const [schools, setSchools] = useState<string[] | null>(null);
  const [fetchSchool, setFetchSchool] = useState<string | null>(() => savedState()?.query.school ?? null);
  const [serverScoped, setServerScoped] = useState(false);
  const loadStartedAt = useRef<number | null>(null);
  const timedGeneration = useRef(-1);
  const [loaded, setLoaded] = useState<Loaded>({ kind: 'loading' });
  const [state, setState] = useState<ListState>(EMPTY_STATE);
  const [generation, setGeneration] = useState(0);

  useEffect(
    () =>
      onProgressReportsReset(() => {
        setLoaded({ kind: 'loading' });
        setState(EMPTY_STATE);
        setSchools(null);
        setFetchSchool(null);
        setGeneration((n) => n + 1);
      }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    if (timedGeneration.current !== generation) {
      timedGeneration.current = generation;
      loadStartedAt.current = performance.now();
    }
    loadProgressReportStudents(rememberedSchools(source), 2, { school: fetchSchool })
      .then((data) => {
        if (cancelled) return;
        const names = data.schools ? data.schools.map(schoolLabel) : schoolsOf(data.students);
        const served = data.school === undefined ? null : schoolLabel(data.school);
        if (served !== null && names.length > 0 && !names.includes(served)) {
          setFetchSchool(null);
          return;
        }
        setSchools(names);
        setServerScoped(served !== null);
        setLoaded({ kind: 'ready', data });
        const saved = savedState() ?? EMPTY_STATE;
        const keepSchool = saved.query.school !== null && names.includes(saved.query.school);
        const school = served ?? (keepSchool ? saved.query.school : (names[0] ?? null));
        const sameSchool = school === saved.query.school;
        const grade = sameSchool && gradeOptions(data.students, school).some((o) => o.value === saved.query.grade) ? saved.query.grade : ALL;
        const teacher = sameSchool && teacherOptions(data.students, school).includes(saved.query.teacher) ? saved.query.teacher : ALL;
        const klass = sameSchool && classOptions(data.students, school).includes(saved.query.class) ? saved.query.class : ALL;
        const benchmark = benchmarkFilterOptions(data.currentWindow).some((o) => o.value === saved.query.benchmark) ? saved.query.benchmark : 'all';
        const query = { ...saved.query, school, grade, teacher, class: klass, benchmark };
        const page = Math.min(query.page, pageCount(visibleRows(data.students, query).length));
        setState({ ...saved, query: { ...query, page } });
      })
      .catch(() => {
        if (cancelled) return;
        setLoaded({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, storage, generation, fetchSchool]);

  // Timed once the settled list has committed, so the wait a person saw
  // includes the render, not only the fetch; a school switch is not timed.
  useEffect(() => {
    if (loaded.kind === 'loading' || loadStartedAt.current === null) return;
    const durationMs = String(Math.max(0, Math.round(performance.now() - loadStartedAt.current)));
    loadStartedAt.current = null;
    logEvent('page.loaded.v1', { page: 'progress-reports', durationMs, status: loaded.kind === 'ready' ? 'ok' : 'error' }).catch(() => {});
  }, [loaded.kind]);

  useEffect(() => {
    if (loaded.kind !== 'ready') return;
    if (storage === undefined) saveListState(state);
    else saveListState(state, storage);
  }, [state, storage, loaded.kind]);

  const students = loaded.kind === 'ready' ? loaded.data.students : NO_STUDENTS;
  const current = loaded.kind === 'ready' ? loaded.data.currentWindow : null;
  const roster = loaded.kind === 'ready' ? (loaded.data.roster ?? null) : null;
  const schoolOptions = schools ?? schoolsOf(students);
  const { query, selected } = state;
  const visible = useMemo(() => visibleRows(students, query), [students, query]);
  const page = Math.min(query.page, pageCount(visible.length, query.rowsPerPage));
  const rows = pageRows(visible, page, query.rowsPerPage);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const visibleIds = visible.map((row) => row.studentId);
  const hidden = hiddenSelectedCount(selected, visibleIds);
  const allVisibleSelected = visible.length > 0 && visible.every((row) => selectedSet.has(row.studentId));

  const setQuery = (patch: Partial<ListQuery>) =>
    setState((prev) => ({ ...prev, query: { ...prev.query, ...patch, page: patch.page ?? 1 } }));
  const setSelected = (next: (ids: string[]) => string[]) => setState((prev) => ({ ...prev, selected: next(prev.selected) }));

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: LIST.surface }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="reports" />
      <Box component="main" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar crumbs={[{ label: 'Reports', to: '/reports' }, { label: 'Student progress reports' }]} />
        <Box data-testid="progress-reports-content" sx={{ p: '24px', maxWidth: PAGE_MAX_WIDTH + 48, width: '100%', mx: 'auto', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', minWidth: 0, flexWrap: 'wrap' }}>
              <Typography component="h1" sx={{ fontFamily: LIST.font, fontSize: 28, fontWeight: 600, lineHeight: '36px', color: LIST.ink }}>
                Student progress reports
              </Typography>
              {loaded.kind === 'ready' && (
                <Typography data-testid="list-meta" sx={{ fontFamily: LIST.font, fontSize: 12, fontWeight: 500, lineHeight: '16px', color: LIST.muted }}>
                  {headerMeta(students, query.school, current, roster)}
                </Typography>
              )}
            </Box>
            <PrintButton count={selected.length} onClick={sections.open} />
          </Box>
          {sections.pending !== null && (
            <PrintSectionsDialog
              open
              title={printLabel(selected.length)}
              groupLabel="Include in each report"
              primaryLabel="Preview reports"
              chosen={sections.pending.chosen}
              onCancel={sections.cancel}
              onConfirm={(chosen) => {
                sections.save(chosen);
                navigate(PRINT_PATH, { state: { sections: sectionIds(chosen) } });
              }}
            />
          )}
          <Box
            data-testid="students-card"
            sx={{ display: 'flex', flexDirection: 'column', gap: '16px', pt: '16px', bgcolor: LIST.cardBg, borderRadius: '12px', boxShadow: LIST.cardShadow }}
          >
            {loaded.kind === 'error' ? (
              <LoadFailure tokens={LIST} />
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: '16px', flexWrap: 'wrap' }}>
                  <FilterSelect
                    label="School"
                    value={query.school ?? ''}
                    options={schoolOptions.map((school) => ({ value: school, label: school }))}
                    onChange={(school) => {
                      const next: ListState = { ...state, query: { ...state.query, school, grade: ALL, teacher: ALL, class: ALL, page: 1 } };
                      setState(next);
                      if (serverScoped) {
                        if (storage === undefined) saveListState(next);
                        else saveListState(next, storage);
                        setLoaded({ kind: 'loading' });
                        setFetchSchool(school);
                      }
                    }}
                  />
                  <FilterSelect
                    label="Grade"
                    value={query.grade}
                    options={[{ value: ALL, label: 'All' }, ...gradeOptions(students, query.school)]}
                    onChange={(grade) => setQuery({ grade })}
                  />
                  <FilterSelect
                    label="Teacher"
                    value={query.teacher}
                    options={[{ value: ALL, label: 'All' }, ...teacherOptions(students, query.school).map((teacher) => ({ value: teacher, label: teacher }))]}
                    onChange={(teacher) => setQuery({ teacher })}
                  />
                  <FilterSelect
                    label="Class"
                    value={query.class}
                    options={[{ value: ALL, label: 'All' }, ...classOptions(students, query.school).map((name) => ({ value: name, label: name }))]}
                    onChange={(name) => setQuery({ class: name })}
                  />
                  <FilterSelect
                    label="Benchmark"
                    value={query.benchmark}
                    options={benchmarkFilterOptions(current)}
                    onChange={(benchmark) => setQuery({ benchmark: benchmark as BenchmarkFilter })}
                  />
                  <TextField
                    size="small"
                    placeholder="Search by student name…"
                    value={query.search}
                    onChange={(event) => setQuery({ search: event.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: LIST.muted, width: 24, height: 24 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      flex: '1 1 0',
                      minWidth: 160,
                      '& .MuiOutlinedInput-root': { height: 36, borderRadius: '8px', fontFamily: LIST.font, fontSize: 16 },
                      '& input::placeholder': { textOverflow: 'ellipsis' },
                    }}
                  />
                </Box>
                {selected.length > 0 && (
                  <SelectionBanner
                    label={bannerLabel({ selected: selected.length, visible: visible.length, hidden })}
                    selectAllLabel={allVisibleSelected || visible.length === 0 ? null : `Select all ${visible.length} students`}
                    onSelectAll={() => setSelected((ids) => withSelected(ids, visibleIds))}
                    onClear={() => setSelected(() => [])}
                  />
                )}
                {loaded.kind === 'loading' && <TableLoading tokens={{ ...LIST, border: LIST.divider }} />}
                {loaded.kind === 'ready' && (
                  <StudentsTable
                    rows={rows}
                    total={visible.length}
                    page={page}
                    current={current}
                    selected={selectedSet}
                    sort={query.sort}
                    onToggleSort={() => setQuery({ sort: query.sort === 'asc' ? 'desc' : 'asc', page })}
                    onToggleRow={(id) => setSelected((ids) => toggleSelected(ids, id))}
                    onTogglePage={(select) => {
                      const ids = rows.map((row) => row.studentId);
                      setSelected((prev) => (select ? withSelected(prev, ids) : withoutSelected(prev, ids)));
                    }}
                    onPage={(next) => setQuery({ page: next })}
                    rowsPerPage={query.rowsPerPage}
                    onRowsPerPage={(rowsPerPage) => setQuery({ rowsPerPage })}
                    empty={
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', px: '16px', py: '48px', textAlign: 'center' }}>
                        <Typography sx={{ fontFamily: LIST.font, fontSize: 16, fontWeight: 700, lineHeight: '24px', color: LIST.ink }}>
                          No students match these filters
                        </Typography>
                        <Typography sx={{ fontFamily: LIST.font, fontSize: 16, fontWeight: 500, lineHeight: '24px', color: LIST.muted }}>
                          {emptyBody(query)}
                        </Typography>
                        <TextButton label="Clear search and filters" onClick={() => setState((prev) => ({ ...prev, query: clearedQuery(prev.query) }))} />
                      </Box>
                    }
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
